import { ContentStatus, SimulationCaseListFragment } from "__generated__/types";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Select, SelectOption } from "common/components/Select";
import { contentStatusFilterValueLabel } from "common/content-status-filter";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { ContentStatusFilterValue } from "domain/constants";
import gql from "graphql-tag";
import React, { useState } from "react";
import { CreateContentFooterBox } from "../common/CreateContentFooterBox";
import { CreateSimulationCase } from "../simulation/CreateSimulationCase";
import { SimulationList } from "../simulation/SimulationList";
import { simulationListFilter } from "../util";

const QUERY = gql`
  query SimulationCaseListFragment {
    simulationCaseList {
      id
      title
      status
    }
  }
`;

export const SimulationAdminPage: React.FC = () => {
  const [contentStatusFilterValue, setContentStatusFilterValue] =
    React.useState<ContentStatusFilterValue>(ContentStatusFilterValue.ALL);
  const [searchQuery, setSearchQuery] = useState("");

  const { data, loadingOrError, refetch } =
    useSimpleQuery<SimulationCaseListFragment>(QUERY, {
      fetchPolicy: "network-only",
    });

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.simulationCaseList) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const filteredList = data.simulationCaseList.filter(
    (item) =>
      simulationListFilter(item, contentStatusFilterValue) &&
      item.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const contentStatusFilterValueToSelectOption = (
    contentStatusFilterValue: ContentStatusFilterValue,
  ): SelectOption => {
    return {
      key: contentStatusFilterValue.toString(),
      value: contentStatusFilterValue.toString(),
      label: contentStatusFilterValueLabel(contentStatusFilterValue),
    };
  };

  return (
    <AdministrationLayout title="Simuleringsbasert læring" context={[]}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <div style={{ width: "40%" }}>
          <Select
            name="status"
            title="Status"
            options={Object.values(ContentStatusFilterValue).map(
              contentStatusFilterValueToSelectOption,
            )}
            value={contentStatusFilterValue}
            onChange={(newValue: string) => {
              setContentStatusFilterValue(newValue as ContentStatusFilterValue);
            }}
          />
        </div>

        <input
          type="text"
          placeholder="Søk etter casetittel..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          style={{
            height: "3vh",
            width: "40%",
            position: "relative",
            alignSelf: "flex-end",
          }}
        />
      </div>

      <CreateContentFooterBox
        buttonText="Opprett ny case"
        content={CreateSimulationCase}
        onUpdated={() => {
          void refetch();
        }}
      />

      {data.simulationCaseList.length === 0 ? (
        <p>Ingen caser eksisterer.</p>
      ) : (
        filteredList.length === 0 && (
          <p>
            Ingen caser eksisterer for status &quot;
            {contentStatusFilterValueLabel(contentStatusFilterValue)}&quot; med
            tittelen &quot;{searchQuery}&quot;
          </p>
        )
      )}
      {filteredList.length > 0 && (
        <SimulationList
          showElementIcons
          elements={filteredList.map((item) => ({
            category: "SimulationCaseElement",
            type: "SimulationCase",
            id: item.id,
            status: item.status ?? ContentStatus.DRAFT,
            title: item.title ?? " ",
          }))}
          complete={function (): void {
            console.log("Complete function called");
          }}
        />
      )}
    </AdministrationLayout>
  );
};
